import React, { useEffect, useMemo, useState, useRef } from 'react';
import { fmtMoney } from '../utils/formatters';
import { RowData } from '../utils/roiCalculations';
interface ProfitChartProps {
  rows: RowData[];
  breakeven: string;
  halfSales: boolean;
  setHalfSales: (val: boolean) => void;
  doubleAttrition: boolean;
  setDoubleAttrition: (val: boolean) => void;
}
type ChartMode = 'cumulative' | 'monthly' | 'roi';
function getNiceAxisValues(min: number, max: number): number[] {
  const range = max - min;
  let step: number;
  if (range <= 100000) {
    step = 25000;
  } else if (range <= 250000) {
    step = 50000;
  } else if (range <= 500000) {
    step = 100000;
  } else if (range <= 1000000) {
    step = 200000;
  } else {
    step = 250000;
  }
  const axisMin = Math.floor(min / step) * step;
  const axisMax = Math.ceil(max / step) * step;
  const values: number[] = [];
  for (let i = 0; i <= 4; i++) {
    values.push(axisMin + (axisMax - axisMin) * (i / 4));
  }
  return values;
}
// Format money for mobile (shorter labels)
function fmtMoneyShort(n: number, isMobile: boolean): string {
  if (!isMobile) return fmtMoney(n);
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 1000000) {
    return `${sign}$${(abs / 1000000).toFixed(1)}M`;
  } else if (abs >= 1000) {
    return `${sign}$${(abs / 1000).toFixed(0)}K`;
  }
  return fmtMoney(n);
}
export function ProfitChart({
  rows,
  breakeven,
  halfSales,
  setHalfSales,
  doubleAttrition,
  setDoubleAttrition
}: ProfitChartProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [hoveredPoint, setHoveredPoint] = useState<number | null>(null);
  const [mode, setMode] = useState<ChartMode>('roi');
  const [timeframe, setTimeframe] = useState<3 | 6 | 12>(6);
  const [mousePos, setMousePos] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  // Detect mobile on mount and window resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  const adjustedRows = useMemo(() => {
    if (!halfSales && !doubleAttrition) return rows;
    return rows.map(row => {
      let newRow = {
        ...row
      };
      if (halfSales) {
        newRow.totalSales = row.totalSales * 0.5;
        newRow.grossRevenue = row.grossRevenue * 0.5;
        newRow.grossProfit = row.grossProfit * 0.5;
      }
      if (doubleAttrition) {
        newRow.launched = row.launched * 0.5;
        newRow.active30d = row.active30d * 0.5;
        newRow.active60d = row.active60d * 0.5;
        newRow.active90d = row.active90d * 0.5;
        newRow.veterans = row.veterans * 0.5;
        const retentionMultiplier = 0.5;
        const salesMultiplier = halfSales ? 0.5 : 1;
        newRow.totalSales = row.totalSales * retentionMultiplier * salesMultiplier;
        newRow.grossRevenue = row.grossRevenue * retentionMultiplier * salesMultiplier;
        newRow.grossProfit = row.grossProfit * retentionMultiplier * salesMultiplier;
      }
      newRow.netProfit = newRow.grossProfit - row.totalCost;
      return newRow;
    });
  }, [rows, halfSales, doubleAttrition]);
  const finalRows = useMemo(() => {
    let cumProfit = adjustedRows[0].cumProfit;
    return adjustedRows.map((row, idx) => {
      if (idx === 0) return row;
      cumProfit += row.netProfit;
      return {
        ...row,
        cumProfit
      };
    });
  }, [adjustedRows]);
  const baseRows = useMemo(() => {
    let cumProfit = rows[0].cumProfit;
    return rows.map((row, idx) => {
      if (idx === 0) return row;
      cumProfit += row.netProfit;
      return {
        ...row,
        cumProfit
      };
    });
  }, [rows]);
  const getCumulativeTotals = (index: number) => {
    const upToIndex = finalRows.slice(0, index + 1);
    const totalInvestment = upToIndex.reduce((sum, r) => sum + r.totalCost, 0);
    const totalGrossRevenue = upToIndex.reduce((sum, r) => sum + r.grossRevenue, 0);
    const totalProfit = upToIndex.reduce((sum, r) => sum + r.netProfit, 0);
    return {
      period: finalRows[index].period,
      totalInvestment,
      totalRevenue: totalGrossRevenue,
      totalProfit,
      monthlyInvestment: finalRows[index].totalCost,
      monthlyRevenue: finalRows[index].grossProfit,
      monthlyProfit: finalRows[index].netProfit
    };
  };
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = 400 * dpr;
    ctx.scale(dpr, dpr);
    const w = rect.width;
    const h = 400;
    ctx.clearRect(0, 0, w, h);
    // Responsive padding
    const padding = {
      l: isMobile ? 50 : 80,
      r: isMobile ? 8 : 16,
      t: 16,
      b: 32
    };
    const innerW = w - padding.l - padding.r;
    const innerH = h - padding.t - padding.b;
    let displayRows: RowData[] = finalRows.slice(0, timeframe + 1);
    let baseDisplayRows: RowData[] = baseRows.slice(0, timeframe + 1);
    let data1: number[];
    let data2: number[] | null = null;
    let minV: number, maxV: number;
    if (mode === 'cumulative') {
      const displayData1 = displayRows.map(r => r.cumProfit);
      const displayCumulativeRevenue: number[] = [];
      let sumRevenue = 0;
      displayRows.forEach(r => {
        sumRevenue += r.grossRevenue;
        displayCumulativeRevenue.push(sumRevenue);
      });
      const displayData2 = displayCumulativeRevenue;
      minV = Math.min(0, ...displayData1, ...displayData2);
      maxV = Math.max(...displayData1, ...displayData2);
      data1 = displayData1;
      data2 = displayData2;
    } else if (mode === 'roi') {
      const displayCumulativeInvestment: number[] = [];
      const displayCumulativeRevenue: number[] = [];
      let sumInvestment = 0;
      let sumRevenue = 0;
      displayRows.forEach(r => {
        sumInvestment += r.totalCost;
        sumRevenue += r.grossRevenue;
        displayCumulativeInvestment.push(sumInvestment);
        displayCumulativeRevenue.push(sumRevenue);
      });
      minV = Math.min(0, ...displayCumulativeInvestment, ...displayCumulativeRevenue);
      maxV = Math.max(...displayCumulativeInvestment, ...displayCumulativeRevenue);
      data1 = displayCumulativeInvestment;
      data2 = displayCumulativeRevenue;
    } else {
      const displayData1 = displayRows.map(r => r.totalCost);
      const displayData2 = displayRows.map(r => r.grossProfit);
      minV = Math.min(0, ...displayData1, ...displayData2);
      maxV = Math.max(...displayData1, ...displayData2);
      data1 = displayData1;
      data2 = displayData2;
    }
    const axisValues = getNiceAxisValues(minV, maxV);
    const lo = axisValues[0];
    const hi = axisValues[axisValues.length - 1];
    const dataLength = displayRows.length;
    const x = (i: number) => padding.l + i / (dataLength - 1) * innerW;
    const y = (v: number) => padding.t + (1 - (v - lo) / (hi - lo)) * innerH;
    // Axes
    ctx.strokeStyle = '#E2E8F0';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(padding.l, padding.t);
    ctx.lineTo(padding.l, padding.t + innerH);
    ctx.lineTo(padding.l + innerW, padding.t + innerH);
    ctx.stroke();
    // Zero line
    if (lo < 0 && hi > 0) {
      const y0 = y(0);
      ctx.strokeStyle = '#CBD5E1';
      ctx.setLineDash([5, 5]);
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(padding.l, y0);
      ctx.lineTo(padding.l + innerW, y0);
      ctx.stroke();
      ctx.setLineDash([]);
    }
    // Draw lines and points based on mode
    const lineWidth = isMobile ? 2.5 : 3;
    const pointRadius = isMobile ? 3 : 4;
    const hoveredPointRadius = isMobile ? 5 : 6;
    if (mode === 'cumulative') {
      ctx.strokeStyle = '#F59E0B';
      ctx.lineWidth = lineWidth;
      ctx.beginPath();
      data1.forEach((v, i) => {
        const px = x(i);
        const py = y(v);
        if (i === 0) ctx.moveTo(px, py);else ctx.lineTo(px, py);
      });
      ctx.stroke();
      if (data2) {
        ctx.strokeStyle = '#10B981';
        ctx.lineWidth = lineWidth;
        ctx.beginPath();
        data2.forEach((v, i) => {
          const px = x(i);
          const py = y(v);
          if (i === 0) ctx.moveTo(px, py);else ctx.lineTo(px, py);
        });
        ctx.stroke();
      }
      ctx.fillStyle = '#F59E0B';
      data1.forEach((v, i) => {
        const px = x(i);
        const py = y(v);
        ctx.beginPath();
        ctx.arc(px, py, hoveredPoint === i ? hoveredPointRadius : pointRadius, 0, Math.PI * 2);
        ctx.fill();
        if (hoveredPoint === i) {
          ctx.strokeStyle = '#FFFFFF';
          ctx.lineWidth = 2;
          ctx.stroke();
        }
      });
      if (data2) {
        ctx.fillStyle = '#10B981';
        data2.forEach((v, i) => {
          const px = x(i);
          const py = y(v);
          ctx.beginPath();
          ctx.arc(px, py, hoveredPoint === i ? hoveredPointRadius : pointRadius, 0, Math.PI * 2);
          ctx.fill();
          if (hoveredPoint === i) {
            ctx.strokeStyle = '#FFFFFF';
            ctx.lineWidth = 2;
            ctx.stroke();
          }
        });
      }
    } else if (mode === 'roi') {
      ctx.strokeStyle = '#EF4444';
      ctx.lineWidth = lineWidth;
      ctx.beginPath();
      data1.forEach((v, i) => {
        const px = x(i);
        const py = y(v);
        if (i === 0) ctx.moveTo(px, py);else ctx.lineTo(px, py);
      });
      ctx.stroke();
      if (data2) {
        ctx.strokeStyle = '#10B981';
        ctx.lineWidth = lineWidth;
        ctx.beginPath();
        data2.forEach((v, i) => {
          const px = x(i);
          const py = y(v);
          if (i === 0) ctx.moveTo(px, py);else ctx.lineTo(px, py);
        });
        ctx.stroke();
      }
      if (data2) {
        for (let i = 1; i < data1.length; i++) {
          if (data2[i] >= data1[i] && data2[i - 1] < data1[i - 1]) {
            const px = x(i);
            const py = y(data2[i]);
            ctx.fillStyle = '#F59E0B';
            ctx.beginPath();
            ctx.arc(px, py, isMobile ? 8 : 10, 0, Math.PI * 2);
            ctx.fill();
            ctx.strokeStyle = '#FFFFFF';
            ctx.lineWidth = 3;
            ctx.stroke();
            ctx.strokeStyle = '#F59E0B';
            ctx.setLineDash([5, 5]);
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.moveTo(px, padding.t);
            ctx.lineTo(px, padding.t + innerH);
            ctx.stroke();
            ctx.setLineDash([]);
            ctx.fillStyle = '#F59E0B';
            ctx.font = `bold ${isMobile ? '11px' : '14px'} Inter, sans-serif`;
            ctx.textAlign = 'center';
            ctx.fillText(isMobile ? 'ROI!' : 'ROI Achieved!', px, padding.t - 5);
            break;
          }
        }
      }
      ctx.fillStyle = '#EF4444';
      data1.forEach((v, i) => {
        const px = x(i);
        const py = y(v);
        ctx.beginPath();
        ctx.arc(px, py, hoveredPoint === i ? hoveredPointRadius : pointRadius, 0, Math.PI * 2);
        ctx.fill();
        if (hoveredPoint === i) {
          ctx.strokeStyle = '#FFFFFF';
          ctx.lineWidth = 2;
          ctx.stroke();
        }
      });
      if (data2) {
        ctx.fillStyle = '#10B981';
        data2.forEach((v, i) => {
          const px = x(i);
          const py = y(v);
          ctx.beginPath();
          ctx.arc(px, py, hoveredPoint === i ? hoveredPointRadius : pointRadius, 0, Math.PI * 2);
          ctx.fill();
          if (hoveredPoint === i) {
            ctx.strokeStyle = '#FFFFFF';
            ctx.lineWidth = 2;
            ctx.stroke();
          }
        });
      }
    } else {
      ctx.strokeStyle = '#EF4444';
      ctx.lineWidth = lineWidth;
      ctx.beginPath();
      data1.forEach((v, i) => {
        const px = x(i);
        const py = y(v);
        if (i === 0) ctx.moveTo(px, py);else ctx.lineTo(px, py);
      });
      ctx.stroke();
      if (data2) {
        ctx.strokeStyle = '#10B981';
        ctx.lineWidth = lineWidth;
        ctx.beginPath();
        data2.forEach((v, i) => {
          const px = x(i);
          const py = y(v);
          if (i === 0) ctx.moveTo(px, py);else ctx.lineTo(px, py);
        });
        ctx.stroke();
      }
      ctx.fillStyle = '#EF4444';
      data1.forEach((v, i) => {
        const px = x(i);
        const py = y(v);
        ctx.beginPath();
        ctx.arc(px, py, hoveredPoint === i ? hoveredPointRadius : pointRadius, 0, Math.PI * 2);
        ctx.fill();
        if (hoveredPoint === i) {
          ctx.strokeStyle = '#FFFFFF';
          ctx.lineWidth = 2;
          ctx.stroke();
        }
      });
      if (data2) {
        ctx.fillStyle = '#10B981';
        data2.forEach((v, i) => {
          const px = x(i);
          const py = y(v);
          ctx.beginPath();
          ctx.arc(px, py, hoveredPoint === i ? hoveredPointRadius : pointRadius, 0, Math.PI * 2);
          ctx.fill();
          if (hoveredPoint === i) {
            ctx.strokeStyle = '#FFFFFF';
            ctx.lineWidth = 2;
            ctx.stroke();
          }
        });
      }
    }
    // Y-Axis Labels - responsive font size and formatting
    ctx.fillStyle = '#475569';
    ctx.font = `600 ${isMobile ? '10px' : '13px'} Inter, sans-serif`;
    ctx.textAlign = 'right';
    axisValues.forEach(tv => {
      const py = y(tv);
      ctx.fillText(fmtMoneyShort(tv, isMobile), padding.l - (isMobile ? 4 : 10), py + 4);
    });
    // X-Axis Labels
    ctx.fillStyle = '#475569';
    ctx.font = `600 ${isMobile ? '10px' : '13px'} Inter, sans-serif`;
    ctx.textAlign = 'left';
    ctx.fillText(isMobile ? 'Start' : 'Launch', padding.l, h - 10);
    ctx.textAlign = 'right';
    ctx.fillText(`M${timeframe}`, padding.l + innerW, h - 10);
  }, [finalRows, baseRows, mode, timeframe, hoveredPoint, isMobile]);
  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const padding = {
      l: isMobile ? 50 : 80,
      r: isMobile ? 8 : 16,
      t: 16,
      b: 32
    };
    const innerW = rect.width - padding.l - padding.r;
    const displayRows = finalRows.slice(0, timeframe + 1);
    const dataLength = displayRows.length;
    let closestIndex = -1;
    let closestDist = Infinity;
    displayRows.forEach((_, i) => {
      const px = padding.l + i / (dataLength - 1) * innerW;
      const dist = Math.abs(mouseX - px);
      if (dist < closestDist && dist < (isMobile ? 30 : 20)) {
        closestDist = dist;
        closestIndex = i;
      }
    });
    setHoveredPoint(closestIndex >= 0 ? closestIndex : null);
    setMousePos(closestIndex >= 0 ? {
      x: e.clientX,
      y: e.clientY
    } : null);
  };
  const handleTouchMove = (e: React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches[0];
    const mouseX = touch.clientX - rect.left;
    const padding = {
      l: 50,
      r: 8,
      t: 16,
      b: 32
    };
    const innerW = rect.width - padding.l - padding.r;
    const displayRows = finalRows.slice(0, timeframe + 1);
    const dataLength = displayRows.length;
    let closestIndex = -1;
    let closestDist = Infinity;
    displayRows.forEach((_, i) => {
      const px = padding.l + i / (dataLength - 1) * innerW;
      const dist = Math.abs(mouseX - px);
      if (dist < closestDist && dist < 30) {
        closestDist = dist;
        closestIndex = i;
      }
    });
    setHoveredPoint(closestIndex >= 0 ? closestIndex : null);
    setMousePos(closestIndex >= 0 ? {
      x: touch.clientX,
      y: touch.clientY
    } : null);
  };
  const handleMouseLeave = () => {
    setHoveredPoint(null);
    setMousePos(null);
  };
  const tooltipData = hoveredPoint !== null ? getCumulativeTotals(hoveredPoint) : null;
  return <div className="p-4 md:p-6 rounded bg-gray-50 border-2 border-gray-200" data-id="element-276">
      {/* Header - Stack on mobile */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-3" data-id="element-277">
        <div className="text-xs md:text-sm text-gray-600 font-semibold" data-id="element-278">
          {mode === 'cumulative' ? 'Cumulative Net Profit' : mode === 'roi' ? `ROI Break-Even (${timeframe}M)` : 'Monthly Investment vs Revenue'}
        </div>

        <div className="flex flex-wrap gap-2" data-id="element-279">
          {/* Timeframe Toggle */}
          <div className="flex gap-1 bg-white border-2 border-gray-200 rounded p-1" data-id="element-280">
            <button onClick={() => setTimeframe(3)} className={`px-2 md:px-3 py-1 rounded text-xs font-semibold transition-colors ${timeframe === 3 ? 'bg-amber text-navy' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-281">
              3M
            </button>
            <button onClick={() => setTimeframe(6)} className={`px-2 md:px-3 py-1 rounded text-xs font-semibold transition-colors ${timeframe === 6 ? 'bg-amber text-navy' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-282">
              6M
            </button>
            <button onClick={() => setTimeframe(12)} className={`px-2 md:px-3 py-1 rounded text-xs font-semibold transition-colors ${timeframe === 12 ? 'bg-amber text-navy' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-283">
              12M
            </button>
          </div>

          {/* Mode Toggle */}
          <div className="flex gap-1 md:gap-2 bg-white border-2 border-gray-200 rounded p-1" data-id="element-284">
            <button onClick={() => setMode('roi')} className={`px-2 md:px-3 py-1 rounded text-xs font-semibold transition-colors ${mode === 'roi' ? 'bg-amber text-navy' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-285">
              ROI
            </button>
            <button onClick={() => setMode('monthly')} className={`px-2 md:px-3 py-1 rounded text-xs font-semibold transition-colors ${mode === 'monthly' ? 'bg-amber text-navy' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-286">
              Monthly
            </button>
            <button onClick={() => setMode('cumulative')} className={`px-2 md:px-3 py-1 rounded text-xs font-semibold transition-colors ${mode === 'cumulative' ? 'bg-amber text-navy' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-287">
              Cumulative
            </button>
          </div>
        </div>
      </div>

      {/* Scenario Toggles - Stack on mobile */}
      <div className="flex flex-wrap gap-2 mb-3" data-id="element-288">
        <button onClick={() => setHalfSales(!halfSales)} className={`px-2 md:px-3 py-1.5 md:py-2 rounded text-xs font-semibold transition-all border-2 ${halfSales ? 'bg-red-500 text-white border-red-600' : 'bg-white text-gray-700 border-gray-300 hover:border-red-400'}`} data-id="element-289">
          {halfSales ? '✓ ' : ''}50% Sales
        </button>
        <button onClick={() => setDoubleAttrition(!doubleAttrition)} className={`px-2 md:px-3 py-1.5 md:py-2 rounded text-xs font-semibold transition-all border-2 ${doubleAttrition ? 'bg-orange-500 text-white border-orange-600' : 'bg-white text-gray-700 border-gray-300 hover:border-orange-400'}`} data-id="element-290">
          {doubleAttrition ? '✓ ' : ''}2x Attrition
        </button>
      </div>

      {/* Legend - Wrap on mobile */}
      {mode === 'monthly' && <div className="flex flex-wrap gap-3 md:gap-4 mb-3 text-xs" data-id="element-291">
          <div className="flex items-center gap-2" data-id="element-292">
            <div className="w-3 h-3 rounded-full bg-red-500" data-id="element-293"></div>
            <span className="text-gray-600 font-semibold" data-id="element-294">Investment</span>
          </div>
          <div className="flex items-center gap-2" data-id="element-295">
            <div className="w-3 h-3 rounded-full bg-green-500" data-id="element-296"></div>
            <span className="text-gray-600 font-semibold" data-id="element-297">Revenue</span>
          </div>
        </div>}

      {mode === 'cumulative' && <div className="flex flex-wrap gap-3 md:gap-4 mb-3 text-xs" data-id="element-298">
          <div className="flex items-center gap-2" data-id="element-299">
            <div className="w-3 h-3 rounded-full bg-amber" data-id="element-300"></div>
            <span className="text-gray-600 font-semibold" data-id="element-301">Net Profit</span>
          </div>
          <div className="flex items-center gap-2" data-id="element-302">
            <div className="w-3 h-3 rounded-full bg-green-500" data-id="element-303"></div>
            <span className="text-gray-600 font-semibold" data-id="element-304">Revenue</span>
          </div>
        </div>}

      {mode === 'roi' && <div className="flex flex-wrap gap-3 md:gap-4 mb-3 text-xs" data-id="element-305">
          <div className="flex items-center gap-2" data-id="element-306">
            <div className="w-3 h-3 rounded-full bg-red-500" data-id="element-307"></div>
            <span className="text-gray-600 font-semibold" data-id="element-308">Investment</span>
          </div>
          <div className="flex items-center gap-2" data-id="element-309">
            <div className="w-3 h-3 rounded-full bg-green-500" data-id="element-310"></div>
            <span className="text-gray-600 font-semibold" data-id="element-311">Revenue</span>
          </div>
        </div>}

      <div ref={containerRef} className="relative" data-id="element-312">
        <canvas ref={canvasRef} className="w-full h-[400px] block cursor-crosshair touch-none" onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave} onTouchMove={handleTouchMove} onTouchEnd={handleMouseLeave} data-id="element-313" />

        {/* Tooltip - Responsive positioning */}
        {tooltipData && mousePos && <div className="fixed z-50 pointer-events-none" style={{
        left: isMobile ? '50%' : mousePos.x + 16,
        top: isMobile ? mousePos.y - 120 : mousePos.y - 16,
        transform: isMobile ? 'translateX(-50%)' : 'none'
      }} data-id="element-314">
            <div className="bg-navy text-white rounded shadow-xl border-2 border-amber p-3 md:p-4 min-w-[240px] md:min-w-[280px]" data-id="element-315">
              <div className="font-bold text-amber mb-2 text-xs md:text-sm" data-id="element-316">
                {tooltipData.period}
              </div>

              {mode === 'cumulative' ? <div className="space-y-1 md:space-y-1.5 text-[10px] md:text-xs" data-id="element-317">
                  <div className="flex justify-between gap-2 md:gap-4" data-id="element-318">
                    <span className="text-gray-300" data-id="element-319">
                      Cumulative Investment:
                    </span>
                    <span className="font-bold" data-id="element-320">
                      {fmtMoney(tooltipData.totalInvestment)}
                    </span>
                  </div>
                  <div className="flex justify-between gap-2 md:gap-4" data-id="element-321">
                    <span className="text-gray-300" data-id="element-322">Cumulative Revenue:</span>
                    <span className="font-bold text-green-400" data-id="element-323">
                      {fmtMoney(tooltipData.totalRevenue)}
                    </span>
                  </div>
                  <div className="border-t border-gray-600 my-1" data-id="element-324"></div>
                  <div className="flex justify-between gap-2 md:gap-4" data-id="element-325">
                    <span className="text-gray-300" data-id="element-326">Net Profit:</span>
                    <span className={`font-bold ${tooltipData.totalProfit >= 0 ? 'text-green-400' : 'text-red-400'}`} data-id="element-327">
                      {fmtMoney(tooltipData.totalProfit)}
                    </span>
                  </div>
                </div> : mode === 'roi' ? <div className="space-y-1 md:space-y-1.5 text-[10px] md:text-xs" data-id="element-328">
                  <div className="flex justify-between gap-2 md:gap-4" data-id="element-329">
                    <span className="text-gray-300" data-id="element-330">Total Invested:</span>
                    <span className="font-bold text-red-400" data-id="element-331">
                      {fmtMoney(tooltipData.totalInvestment)}
                    </span>
                  </div>
                  <div className="flex justify-between gap-2 md:gap-4" data-id="element-332">
                    <span className="text-gray-300" data-id="element-333">Total Revenue:</span>
                    <span className="font-bold text-green-400" data-id="element-334">
                      {fmtMoney(tooltipData.totalRevenue)}
                    </span>
                  </div>
                  <div className="border-t border-gray-600 my-1" data-id="element-335"></div>
                  <div className="flex justify-between gap-2 md:gap-4" data-id="element-336">
                    <span className="text-gray-300" data-id="element-337">Gap:</span>
                    <span className={`font-bold ${tooltipData.totalRevenue >= tooltipData.totalInvestment ? 'text-green-400' : 'text-amber'}`} data-id="element-338">
                      {tooltipData.totalRevenue >= tooltipData.totalInvestment ? '+' : ''}
                      {fmtMoney(tooltipData.totalRevenue - tooltipData.totalInvestment)}
                    </span>
                  </div>
                  {tooltipData.totalRevenue >= tooltipData.totalInvestment && <div className="text-green-400 font-bold text-center mt-1.5 text-xs" data-id="element-339">
                      ✓ ROI Achieved!
                    </div>}
                </div> : <div className="space-y-1 md:space-y-1.5 text-[10px] md:text-xs" data-id="element-340">
                  <div className="flex justify-between gap-2 md:gap-4" data-id="element-341">
                    <span className="text-gray-300" data-id="element-342">Monthly Investment:</span>
                    <span className="font-bold text-red-400" data-id="element-343">
                      {fmtMoney(tooltipData.monthlyInvestment)}
                    </span>
                  </div>
                  <div className="flex justify-between gap-2 md:gap-4" data-id="element-344">
                    <span className="text-gray-300" data-id="element-345">Monthly Revenue:</span>
                    <span className="font-bold text-green-400" data-id="element-346">
                      {fmtMoney(tooltipData.monthlyRevenue)}
                    </span>
                  </div>
                  <div className="border-t border-gray-600 my-1" data-id="element-347"></div>
                  <div className="flex justify-between gap-2 md:gap-4" data-id="element-348">
                    <span className="text-gray-300" data-id="element-349">Monthly Profit:</span>
                    <span className={`font-bold ${tooltipData.monthlyProfit >= 0 ? 'text-green-400' : 'text-red-400'}`} data-id="element-350">
                      {fmtMoney(tooltipData.monthlyProfit)}
                    </span>
                  </div>
                </div>}
            </div>
          </div>}
      </div>

      <div className="text-xs md:text-sm text-gray-600 mt-3" data-id="element-351">
        {mode === 'cumulative' ? <>
            <span className="font-semibold" data-id="element-352">Break-even:</span> {breakeven}
          </> : mode === 'roi' ? <span data-id="element-353">When revenue exceeds investment</span> : <span data-id="element-354">Lines cross when revenue exceeds investment</span>}
      </div>
    </div>;
}